import { evaluateVariantHours } from './productHours';

export interface PricingLine {
  code: string;
  description: string;
  hours: number;
  formula?: string;
  rateCents: number;
  totalCents: number;
  hidden?: boolean;
}

export interface PricingVariant {
  id: string;
  name: string;
  descriptionHtml?: string;
  priceCents: number | null;
  lines: PricingLine[];
}

/** Private quote snapshot. Never serialize this structure to a public route. */
export interface InvestmentProduct {
  schemaVersion: 1;
  id: string;
  productId: string;
  productVersionId: string;
  productName: string;
  priceMode: 'hours' | 'fixed' | 'on_request';
  variants: PricingVariant[];
  /** Originele opbouw bewaren zodra sales regels toevoegt of verplaatst. */
  catalogVariants?: PricingVariant[];
  variantId: string;
  /**
   * Van–tot prijs: de offerte toont een bandbreedte van variantId tot deze variant,
   * bijvoorbeeld zolang de klant nog kiest tussen 6, 8 of 10 uur.
   */
  rangeVariantId?: string;
  display: 'collapsed' | 'expanded';
  showHours: boolean;
  quantity: number;
  discountPct: number;
  /** rangeHours zijn de uren aan de tot-kant; tarief en meetellen gelden voor beide kanten. */
  overrides: Record<string, { hours?: number; rangeHours?: number; rateCents?: number; excluded?: boolean }>;
  amountCents?: number;
  /** Tot-bedrag van een van–tot prijs zonder urenopbouw. */
  rangeAmountCents?: number;
  /** Tekst die bij het product is ingesteld voor een prijs op aanvraag, bv. "Vanaf € 99 per maand". */
  onRequestText?: string;
  /** Vrije tekst van sales in plaats van een bedrag, bv. "Op maat". */
  amountText?: string;
  /** Offertetekst incl. titel; vervangt de omschrijving van de variant. Leeg toont alleen de productnaam. */
  descriptionHtml?: string;
}

export interface PublicProductPrice {
  display: 'collapsed' | 'expanded';
  showHours: boolean;
  descriptionHtml?: string;
  /** Bij een van–tot prijs de onderkant. */
  totalCents: number | null;
  /** Bovenkant van een van–tot prijs; alleen gezet wanneer die hoger is dan totalCents. */
  maxTotalCents?: number;
  /** Tekst in plaats van het bedrag; alleen gezet wanneer totalCents null is. */
  priceText?: string;
  lines: Array<{
    description: string;
    totalCents: number;
    maxTotalCents?: number;
    hours?: number;
    maxHours?: number;
    rateCents?: number;
  }>;
}

const finite = (value: number) => Number.isFinite(value) && value >= 0;

/**
 * "€ 2.300,00 – € 3.450,00", of één bedrag wanneer beide kanten gelijk zijn. Het streepje
 * blijft bij de onderkant, zodat een smalle bedragkolom netjes over twee regels breekt.
 */
export function formatRange<T extends number>(min: T, max: T | null | undefined, format: (value: T) => string) {
  return max == null || max === min ? format(min) : `${format(min)}\u00a0– ${format(max)}`;
}

/** Onder- en bovenkant op volgorde, wat de klant ook als van- en tot-variant koos. */
const bounds = (from: number, to: number | null | undefined) =>
  to == null ? { min: from, max: from } : { min: Math.min(from, to), max: Math.max(from, to) };

/** Variant van de tot-kant, of niets wanneer het product één prijs heeft. */
export function investmentProductRangeVariant(product: InvestmentProduct) {
  if (!product.rangeVariantId || product.rangeVariantId === product.variantId) return undefined;
  return product.variants.find((entry) => entry.id === product.rangeVariantId);
}

/** Tekst bij een catalogusprijs zonder bedrag, zoals ingesteld bij het product. */
export const investmentProductCatalogText = (product: InvestmentProduct) =>
  product.onRequestText?.trim() || 'Op aanvraag';

/** Tekst in de offerte wanneer er geen bedrag is: wat sales invulde, anders de catalogustekst. */
export const investmentProductPriceText = (product: InvestmentProduct) =>
  product.amountText?.trim() || investmentProductCatalogText(product);

/**
 * Rekent de regels van de van-variant door met de uren van `catalog`. Aan de tot-kant
 * komen uren en formules uit de tot-variant; een regel die sales zelf toevoegde bestaat
 * daar niet en houdt zijn van-uren tot er tot-uren zijn ingevuld.
 */
function priceLines(
  product: InvestmentProduct,
  lines: PricingLine[],
  catalog: PricingVariant,
  side: 'from' | 'to',
  multiplier: number,
) {
  const source = (line: PricingLine) =>
    side === 'from' ? line : catalog.lines.find((entry) => entry.code === line.code);
  const sources = lines.map((line) => {
    const override = product.overrides[line.code] ?? {};
    const own = source(line);
    const hours = side === 'from' ? override.hours : override.rangeHours ?? (own ? undefined : override.hours);
    if (hours != null && !finite(hours)) throw new Error('Ongeldige uren');
    if (override.rateCents != null && !finite(override.rateCents)) throw new Error('Ongeldig tarief');
    const base = own ?? line;
    return {
      id: line.code, code: line.code, description: line.description, rateKey: line.code,
      hours: { quote: override.excluded ? 0 : hours ?? base.formula ?? base.hours },
    };
  });
  // Regels die alleen in de tot-variant staan, kunnen in diens formules voorkomen.
  const priced = new Set(lines.map((line) => line.code));
  const rangeOnly = side === 'to'
    ? catalog.lines.filter((line) => !priced.has(line.code)).map((line) => ({
      id: line.code, code: line.code, description: line.description, rateKey: line.code,
      hours: { quote: line.formula ?? line.hours },
    }))
    : [];
  const evaluated = evaluateVariantHours([...sources, ...rangeOnly], 'quote');
  if (evaluated.errors.length) throw new Error(evaluated.errors.map((error) => error.message).join(', '));
  return lines.map((line) => {
    const override = product.overrides[line.code] ?? {};
    const hours = evaluated.hoursByCode[line.code] ?? 0;
    if (!finite(hours)) throw new Error('Formule geeft ongeldige uren');
    const rateCents = override.rateCents ?? line.rateCents;
    const base = product.priceMode === 'hours' ? Math.round(hours * rateCents) : (source(line) ?? line).totalCents;
    return { ...line, hours, rateCents, excluded: override.excluded === true, totalCents: Math.round(base * multiplier) };
  });
}

function variantTotal(
  product: InvestmentProduct,
  catalog: PricingVariant,
  lines: ReturnType<typeof priceLines>,
  amountCents: number | undefined,
  multiplier: number,
) {
  // Een regel die alleen in de tot-variant staat, telt mee in deze rest van de catalogusprijs.
  const priced = new Set(lines.map((line) => line.code));
  const remainder = (catalog.priceCents ?? 0) - catalog.lines
    .filter((line) => priced.has(line.code))
    .reduce((sum, line) => sum + line.totalCents, 0);
  const lineTotal = lines.filter((line) => !line.excluded).reduce((sum, line) => sum + line.totalCents, 0);
  return product.priceMode === 'hours' && lines.length
    ? lineTotal + Math.round(remainder * multiplier)
    : amountCents != null
      ? Math.round(amountCents * multiplier)
      : catalog.priceCents === null ? null : Math.round(catalog.priceCents * multiplier);
}

type PricedLine = ReturnType<typeof priceLines>[number] & { rangeHours?: number; rangeTotalCents?: number };

export function calculateInvestmentProduct(product: InvestmentProduct) {
  if (!finite(product.quantity) || product.quantity <= 0) throw new Error('Aantal moet groter zijn dan nul');
  if (!finite(product.discountPct) || product.discountPct > 100) throw new Error('Korting moet tussen 0 en 100 liggen');
  if (product.amountCents != null && !finite(product.amountCents)) throw new Error('Ongeldig bedrag');
  if (product.rangeAmountCents != null && !finite(product.rangeAmountCents)) throw new Error('Ongeldig tot-bedrag');
  const variant = product.variants.find((entry) => entry.id === product.variantId);
  if (!variant) throw new Error('Onbekende variant');
  const multiplier = product.quantity * (1 - product.discountPct / 100);
  const fromLines = priceLines(product, variant.lines, variant, 'from', multiplier);
  if (fromLines.length && fromLines.every((line) => line.excluded)) throw new Error('Minstens één regel moet meetellen');
  const totalCents = variantTotal(product, variant, fromLines, product.amountCents, multiplier);
  const rangeVariant = investmentProductRangeVariant(product);
  const toLines = rangeVariant && priceLines(product, variant.lines, rangeVariant, 'to', multiplier);
  const toTotal = rangeVariant && toLines
    ? variantTotal(product, rangeVariant, toLines, product.rangeAmountCents, multiplier)
    : null;
  // Zonder bedrag aan de van-kant is er geen bandbreedte, alleen de prijstekst.
  const rangeTotalCents = totalCents === null ? null : toTotal;
  const lines: PricedLine[] = toLines && rangeTotalCents !== null
    ? fromLines.map((line, index) => ({ ...line, rangeHours: toLines[index].hours, rangeTotalCents: toLines[index].totalCents }))
    : fromLines;
  const catalogCents = variant.priceCents;
  const differenceCents = totalCents === null || catalogCents === null ? null : totalCents - catalogCents;
  const rangeCatalogCents = rangeTotalCents === null ? null : rangeVariant?.priceCents ?? null;
  const rangeDifferenceCents = rangeTotalCents === null || rangeCatalogCents === null ? null : rangeTotalCents - rangeCatalogCents;
  const range = totalCents === null ? null : bounds(totalCents, rangeTotalCents);
  const priceText = totalCents === null ? investmentProductPriceText(product) : null;
  return {
    lines, totalCents, rangeTotalCents,
    /** Wat het product minimaal en maximaal kost; gelijk zonder van–tot prijs, null zonder bedrag. */
    minCents: range?.min ?? null, maxCents: range?.max ?? null,
    priceText, catalogCents, differenceCents, rangeCatalogCents,
    adjusted: Boolean(differenceCents) || Boolean(rangeDifferenceCents),
  };
}

export function investmentProductDescriptionHtml(product: InvestmentProduct) {
  return product.descriptionHtml ?? product.variants.find((variant) => variant.id === product.variantId)?.descriptionHtml;
}

/** Allowlist of customer-visible values; excluded rows and catalog data stay private. */
export function publicInvestmentProduct(product: InvestmentProduct): PublicProductPrice {
  const computed = calculateInvestmentProduct(product);
  const descriptionHtml = investmentProductDescriptionHtml(product);
  const showHours = product.display === 'expanded' && product.showHours && product.priceMode === 'hours';
  const ranged = computed.rangeTotalCents !== null;
  const visible = product.display === 'expanded' ? computed.lines.filter((line) => !line.excluded && !line.hidden) : [];
  const lines: PublicProductPrice['lines'] = visible.map((line) => {
    const amount = bounds(line.totalCents, line.rangeTotalCents);
    const hours = bounds(line.hours * product.quantity, line.rangeHours == null ? null : line.rangeHours * product.quantity);
    return { description: line.description, totalCents: amount.min,
      ...(amount.max !== amount.min ? { maxTotalCents: amount.max } : {}),
      ...(showHours ? {
        hours: hours.min, ...(hours.max !== hours.min ? { maxHours: hours.max } : {}),
        rateCents: line.rateCents * (1 - product.discountPct / 100),
      } : {}),
    };
  });
  if (lines.length && computed.totalCents !== null) {
    const remainder = bounds(
      computed.totalCents - visible.reduce((sum, line) => sum + line.totalCents, 0),
      ranged ? computed.rangeTotalCents! - visible.reduce((sum, line) => sum + (line.rangeTotalCents ?? 0), 0) : null,
    );
    if (remainder.min || remainder.max) lines.push({ description: 'Overige kosten', totalCents: remainder.min,
      ...(remainder.max !== remainder.min ? { maxTotalCents: remainder.max } : {}) });
  }
  return { display: product.display, showHours, totalCents: computed.minCents, lines,
    ...(computed.maxCents !== null && computed.maxCents !== computed.minCents ? { maxTotalCents: computed.maxCents } : {}),
    ...(computed.priceText !== null ? { priceText: computed.priceText } : {}),
    ...(descriptionHtml ? { descriptionHtml } : {}),
  };
}

export function resetInvestmentProduct(product: InvestmentProduct): InvestmentProduct {
  const { amountCents: _amount, amountText: _text, rangeAmountCents: _rangeAmount, catalogVariants, ...rest } = product;
  return { ...rest, variants: catalogVariants ?? product.variants, quantity: 1, discountPct: 0, overrides: {} };
}
